/**
 * HTML5 drag-and-drop from the legend onto the canvas.
 *
 * Drag from canvas tokens (mouse-based) is handled directly in canvas.ts —
 * it can't use HTML5 drag because canvas tokens aren't DOM elements.
 *
 * Flow:
 *   1. User starts dragging a legend entry. legend.ts attaches the token id
 *      to dataTransfer under MIME `application/x-mapnmove-token`.
 *   2. dragover on canvas → preventDefault to allow drop, set dropEffect.
 *   3. drop on canvas → read token id, convert client coords to grid using
 *      the cell size from the canvas controller, call updateToken.
 */

import * as api from './api.js';
import { screenToGrid } from './grid.js';
import { LEGEND_DRAG_MIME } from './legend.js';
import { isCellOccupied } from './occupancy.js';
import { isPassable } from './overlay.js';
import { getState, upsertToken } from './state.js';

interface CanvasHandle {
  getCellPx: () => number | null;
}

export function mountDragDrop(canvas: HTMLCanvasElement, controller: CanvasHandle): { destroy: () => void } {
  const onDragOver = (e: DragEvent): void => {
    if (e.dataTransfer === null) return;
    if (!Array.from(e.dataTransfer.types).includes(LEGEND_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e: DragEvent): void => {
    if (e.dataTransfer === null) return;
    const raw = e.dataTransfer.getData(LEGEND_DRAG_MIME);
    if (raw === '') return;
    e.preventDefault();
    const tokenId = Number(raw);
    if (!Number.isFinite(tokenId)) return;

    const cell = controller.getCellPx();
    if (cell === null) return;

    const rect = canvas.getBoundingClientRect();
    const { grid_x, grid_y } = screenToGrid(
      e.clientX - rect.left,
      e.clientY - rect.top,
      cell,
      getState().viewport,
    );

    // Pre-check passability + occupancy so we don't even hit the API on a guaranteed reject.
    // The server still validates — this is just to avoid a useless round-trip
    // and to surface the message immediately.
    const state = getState();
    const overlay = state.currentMap?.overlay ?? null;
    if (!isPassable(overlay, grid_x, grid_y)) {
      console.warn('Cannot place token on a wall or closed door');
      return;
    }
    if (isCellOccupied(state.tokens, grid_x, grid_y, tokenId)) {
      console.warn('Cannot place token on an occupied cell');
      return;
    }

    // Dropping from the legend always places the token on the canvas; the
    // server marks it placed so it starts rendering on the map.
    api.updateToken(tokenId, { grid_x, grid_y, is_placed: true })
      .then((updated) => upsertToken(updated))
      .catch((err) => {
        console.error('Failed to place token from legend', err);
      });
  };

  canvas.addEventListener('dragover', onDragOver);
  canvas.addEventListener('drop', onDrop);

  return {
    destroy(): void {
      canvas.removeEventListener('dragover', onDragOver);
      canvas.removeEventListener('drop', onDrop);
    },
  };
}
